import React, { useCallback, useContext } from 'react';
import { useLocation } from '@reach/router';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import Colors from '@components/shared/Colors';
import { PAD_STACK_COUNTS } from '@constants';
import { BoxItem, CartItem, PadSizeType, SubscriptionCartItem } from '@contracts';
import { replaceText } from '@utils/handleTextReplacement';
import { getAllPadTypes, isPadsStackBox } from '@utils/pads';

interface Props {
  boxItems: BoxItem[];
  cartItem: CartItem | SubscriptionCartItem;
}

const CartItemPadsBoxItems: React.FC<Props> = ({ boxItems, cartItem }) => {
  const { t } = useTranslation();
  const {
    state: { country }
  } = useContext(Context);
  const location = useLocation();
  const isStackBox = isPadsStackBox(country);
  const multiplier = 'selectedPlan' in cartItem ? cartItem.selectedPlan?.subscriptionInterval?.length : 1;
  const isMultiplied = isStackBox && multiplier > 1;

  const getInitialOutsideMultiplications = useCallback(() => {
    if (isMultiplied) {
      return (
        <>
          <span data-testid="outside-multiplier">{multiplier}</span>&times; (
        </>
      );
    }

    return null;
  }, [isMultiplied]);

  const getFinalOutsideMultiplications = useCallback(() => {
    return isMultiplied && <>)</>;
  }, [isMultiplied]);

  const getInnerMultiplications = useCallback(
    (boxItem: BoxItem) => {
      const padTypeKey = boxItem.key as PadSizeType;
      const allPadSizeTypes = getAllPadTypes();

      if (isStackBox && allPadSizeTypes.includes(padTypeKey)) {
        const firstMultiplier = boxItem.count / PAD_STACK_COUNTS[padTypeKey];
        const secondMultiplier = boxItem.count / firstMultiplier;

        return (
          <>
            <span data-testid="first-multiplier">{firstMultiplier}</span>&times; <span data-testid="second-multiplier">{secondMultiplier}</span>
          </>
        );
      }

      return (
        <>
          <span data-testid="first-multiplier">{boxItem.count}</span>
        </>
      );
    },
    [isStackBox]
  );

  return (
    <BoxItems>
      {boxItems
        .filter((bi) => bi.count > 0)
        .map((boxItem) => {
          const boxItemTitle = replaceText(t(`components.${boxItem.key}`), country, location);

          return (
            <PadsBoxItem key={boxItem.key} data-testid={`pad-box-title-${boxItem.key}`} data-title={boxItemTitle}>
              {getInitialOutsideMultiplications()}
              {getInnerMultiplications(boxItem)}
              {boxItemTitle}
              {getFinalOutsideMultiplications()}
            </PadsBoxItem>
          );
        })}
    </BoxItems>
  );
};

export default CartItemPadsBoxItems;

const BoxItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
`;

const PadsBoxItem = styled.div`
  font-family: BrownStd;
  font-size: 12px;
  line-height: 22px;
  letter-spacing: -0.03em;
  margin-right: 5px;
  text-align: left;
  color: ${Colors.navyBlue};

  span {
    font-weight: bold;
    margin-right: 4px;
  }
`;
